import { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { paqueteService, Paquete } from '../../services/paqueteService';
import { sucursalService } from '../../services/sucursalService';
import PaqueteForm from '../../components/Paquetes/PaqueteForm';
import PaqueteDetail from '../../components/Paquetes/PaqueteDetail';

const PaquetesList = () => {
  const [page, setPage] = useState(1);
  const [estadoFilter, setEstadoFilter] = useState<string>('');
  const [tipoFilter, setTipoFilter] = useState<string>('');
  const [sucursalFilter, setSucursalFilter] = useState<number | undefined>(undefined);
  const [trackingSearch, setTrackingSearch] = useState<string>('');
  const [showForm, setShowForm] = useState(false);
  const [selectedPaquete, setSelectedPaquete] = useState<Paquete | null>(null);
  const queryClient = useQueryClient();

  const { data: sucursalesData } = useQuery(
    'sucursales',
    () => sucursalService.getAll(1, 100)
  );

  const { data, isLoading } = useQuery(
    ['paquetes', page, estadoFilter, tipoFilter, sucursalFilter, trackingSearch],
    () => paqueteService.getAll(
      page,
      20,
      estadoFilter || undefined,
      tipoFilter || undefined,
      sucursalFilter,
      undefined,
      trackingSearch || undefined
    )
  );

  const handleView = (paquete: Paquete) => {
    setSelectedPaquete(paquete);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    queryClient.invalidateQueries(['paquetes']);
  };

  const handleCloseDetail = () => {
    setSelectedPaquete(null);
    queryClient.invalidateQueries(['paquetes']);
  };

  if (isLoading) {
    return <div className="text-center py-8">Cargando...</div>;
  }

  const estados = [
    { value: '', label: 'Todos' },
    { value: 'registrado', label: 'Registrado' },
    { value: 'en_almacen', label: 'En Almacén' },
    { value: 'en_transito', label: 'En Tránsito' },
    { value: 'entregado', label: 'Entregado' },
    { value: 'devuelto', label: 'Devuelto' },
    { value: 'cancelado', label: 'Cancelado' }
  ];

  const tipos = [
    { value: '', label: 'Todos' },
    { value: 'interno', label: 'Interno' },
    { value: 'externo', label: 'Externo' }
  ];

  const getEstadoColor = (estado: string) => {
    const colors: { [key: string]: string } = {
      registrado: 'bg-gray-100 text-gray-800',
      en_almacen: 'bg-blue-100 text-blue-800',
      en_transito: 'bg-yellow-100 text-yellow-800',
      entregado: 'bg-green-100 text-green-800',
      devuelto: 'bg-orange-100 text-orange-800',
      cancelado: 'bg-red-100 text-red-800'
    };
    return colors[estado] || 'bg-gray-100 text-gray-800';
  };

  const getTipoColor = (tipo: string) => {
    return tipo === 'interno' 
      ? 'bg-purple-100 text-purple-800'
      : 'bg-indigo-100 text-indigo-800';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">Paquetes / Encomiendas</h1>
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 transition"
        >
          + Nuevo Paquete
        </button>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b space-y-4">
          <div className="flex space-x-4 flex-wrap">
            <input
              type="text"
              placeholder="Buscar por tracking..."
              value={trackingSearch}
              onChange={(e) => {
                setTrackingSearch(e.target.value);
                setPage(1);
              }}
              className="px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <select
              value={estadoFilter}
              onChange={(e) => {
                setEstadoFilter(e.target.value);
                setPage(1);
              }}
              className="px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {estados.map((estado) => (
                <option key={estado.value} value={estado.value}>
                  {estado.label}
                </option>
              ))}
            </select>
            <select
              value={tipoFilter}
              onChange={(e) => {
                setTipoFilter(e.target.value);
                setPage(1);
              }}
              className="px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {tipos.map((tipo) => (
                <option key={tipo.value} value={tipo.value}>
                  {tipo.label}
                </option>
              ))}
            </select>
            <select
              value={sucursalFilter || ''}
              onChange={(e) => {
                setSucursalFilter(e.target.value ? Number(e.target.value) : undefined);
                setPage(1);
              }}
              className="px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Todas las sucursales</option>
              {sucursalesData?.data.sucursales.map((sucursal) => (
                <option key={sucursal.id} value={sucursal.id}>
                  {sucursal.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tracking</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Destinatario</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sucursal</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Días</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha Entrada</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data?.data.paquetes && data.data.paquetes.length > 0 ? (
                data.data.paquetes.map((paquete) => (
                  <tr key={paquete.id} className={paquete.diasAlmacenaje >= 7 ? 'bg-yellow-50' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {paquete.numeroTracking}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${getTipoColor(paquete.tipo)}`}>
                        {paquete.tipo.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {paquete.destinatarioNombre}
                      {paquete.destinatarioTelefono && (
                        <div className="text-xs text-gray-500">{paquete.destinatarioTelefono}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {paquete.sucursal?.name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${getEstadoColor(paquete.estado)}`}>
                        {paquete.estado.replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className={paquete.diasAlmacenaje >= 7 ? 'font-bold text-red-600' : ''}>
                        {paquete.diasAlmacenaje} días
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(paquete.fechaEntrada).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleView(paquete)}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        Ver
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="px-6 py-4 text-center text-sm text-gray-500">
                    No se encontraron paquetes
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {data?.data.pagination && (
          <div className="p-4 border-t flex justify-between items-center">
            <div className="text-sm text-gray-700">
              Mostrando {((page - 1) * 20) + 1} a {Math.min(page * 20, data.data.pagination.total)} de {data.data.pagination.total}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Anterior
              </button>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={page >= data.data.pagination.totalPages}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>

      {showForm && (
        <PaqueteForm
          onClose={handleCloseForm}
        />
      )}

      {selectedPaquete && (
        <PaqueteDetail
          paquete={selectedPaquete}
          onClose={handleCloseDetail}
        />
      )}
    </div>
  );
};

export default PaquetesList;

